import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { getAuth } from 'firebase/auth';
import {
  addDoc,
  collection,
  getDocs,
  serverTimestamp,
} from 'firebase/firestore';
import PropTypes from 'prop-types';

import { db } from '@/Firebase';

const FormContext = createContext();

export const useFormContext = () => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error('useFormContext must be used within a FormProvider');
  }
  return context;
};

export const FormProvider = ({ children, defaultFormData, collectionName }) => {
  const [formData, setFormData]         = useState(defaultFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError]   = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [allFormData, setAllFormData]   = useState([]);
  const [fetchError, setFetchError]     = useState(null);
  const [isFetching, setIsFetching]     = useState(false);

  const sendDataToFirebase = async () => {
    try {
      setIsSubmitting(true);
      setSubmitError(null);
      setSubmitSuccess(false);

      // FIX : récupère user au moment de l'appel, pas au render
      // auth.currentUser peut être null au render si Firebase n'a pas encore chargé
      const auth = getAuth();
      const user = auth.currentUser;

      const dataToSubmit = { ...formData };

      if (dataToSubmit.isEntreprise) {
        // delete dataToSubmit.familyComposition;
      }

      // FIX : ajoute userId + createdAt (requis pour la cloud function maison)
if (!user) {
  throw new Error("Utilisateur non connecté");
}
dataToSubmit.userId = user.uid;
dataToSubmit.userEmail = user?.email || 'no-email';
      dataToSubmit.userEpay  = false;
      dataToSubmit.createdAt = serverTimestamp(); // ← requis pour orderBy('createdAt')

      const docRef = await addDoc(collection(db, collectionName), dataToSubmit);
      console.log('Document added with ID:', docRef.id, '| userId:', dataToSubmit.userId);

      setSubmitSuccess(true);
      return docRef.id;

    } catch (error) {
      console.error('Error adding document:', error);
      setSubmitError(error.message || 'Error submitting data.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm        = () => setFormData(defaultFormData);
  const clearSubmitError = () => setSubmitError(null);

  const fetchAllFormData = useCallback(async () => {
    setIsFetching(true);
    try {
      const querySnapshot = await getDocs(collection(db, collectionName));
      const formDataList  = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAllFormData(formDataList);
      setFetchError(null);
    } catch (error) {
      console.error('Error fetching data:', error);
      setFetchError('Error fetching data.');
    } finally {
      setIsFetching(false);
    }
  }, [collectionName]);

  useEffect(() => {
    fetchAllFormData();
  }, [fetchAllFormData]);

  const value = {
    formData,
    setFormData,
    sendDataToFirebase,
    resetForm,
    clearSubmitError,
    isSubmitting,
    submitError,
    submitSuccess,
    allFormData,
    fetchError,
    isFetching,
  };

  return <FormContext.Provider value={value}>{children}</FormContext.Provider>;
};

FormProvider.propTypes = {
  children:        PropTypes.node.isRequired,
  defaultFormData: PropTypes.object.isRequired,
  collectionName:  PropTypes.string.isRequired,
};